/**
 * saddle-variant-fix.js  (drop-in replacement)
 *
 * WHY THIS EXISTS
 * Boots + Saddles render a custom swatch picker that REPLACES the theme's native
 * <variant-picker> element. Because that native element is gone on these pages,
 * the theme's variant-change plumbing never fires, so the dynamic checkout
 * (Shop Pay / "More payment options") button never learns which variant is
 * selected and stays disabled. Add to Cart still works because it's a plain form
 * submit; the accelerated checkout button is not.
 *
 * WHAT THIS DOES
 *   1. Resolves the selected combo -> variant object from /products/{handle}.js
 *   2. Writes that variant id into the form's [name="id"] (works whether the
 *      field is nested in the form or only form-ASSOCIATED via form="...")
 *   3. Nudges the accelerated checkout button to re-read the selection by
 *      dispatching a bubbling change on the FORM itself, firing the theme's
 *      variant event, and (as a gated fallback) clearing a stuck disabled state.
 *
 * ONE KNOB TO CONFIRM: THEME_VARIANT_EVENT below. See notes at the bottom for
 * how to discover the real name on a buckle page in ~30 seconds.
 */
(() => {
  if (!location.pathname.includes("/products/")) return;

  // If your theme uses a different variant-change event name, change it here.
  // (Impact/Prestige/Pinnacle have historically used "variant:change".)
  const THEME_VARIANT_EVENT = "variant:change";

  let productData = null;

  const norm = (s) => String(s ?? "").trim();

  const getHandle = () => {
    const parts = location.pathname.split("/products/");
    return parts[1] ? parts[1].split("/")[0] : null;
  };

  const loadProductJson = async () => {
    const handle = getHandle();
    if (!handle) return null;
    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) return null;
    return await res.json();
  };

  // Read selected option values from the custom swatches, with radio fallback.
  const getSelectedOptionValues = () => {
    const expectedCount = productData?.options?.length || 3;

    const swatchValues = [...document.querySelectorAll(".block-swatch.is-selected")]
      .map((btn) => ({
        idx: Number(btn.getAttribute("data-option-index")),
        val: btn.getAttribute("data-option-value") || btn.dataset.optionValue || btn.textContent,
      }))
      .filter((x) => norm(x.val) && !Number.isNaN(x.idx))
      .sort((a, b) => a.idx - b.idx)
      .map((x) => norm(x.val));

    if (swatchValues.length === expectedCount) return swatchValues;

    const radioValues = [...document.querySelectorAll("input[data-option-position]:checked")]
      .map((el) => ({ pos: Number(el.getAttribute("data-option-position")), val: el.value }))
      .filter((x) => norm(x.val) && !Number.isNaN(x.pos))
      .sort((a, b) => a.pos - b.pos)
      .map((x) => norm(x.val));

    if (radioValues.length === expectedCount) return radioValues;

    return null; // partial selection -> do nothing
  };

  const findMatchingVariant = (options) => {
    if (!productData?.variants?.length || !options?.length) return null;

    // Exact match
    let match = productData.variants.find(
      (v) =>
        v.options?.length === options.length &&
        v.options.every((opt, i) => norm(opt) === norm(options[i]))
    );
    if (match) return match;

    // Tolerant match (spacing / case) in case swatch labels differ slightly
    const up = (s) => norm(s).replace(/\s+/g, " ").toUpperCase();
    match = productData.variants.find(
      (v) =>
        v.options?.length === options.length &&
        v.options.every((opt, i) => up(opt) === up(options[i]))
    );
    return match || null;
  };

  // Find the id field even when it is form-ASSOCIATED (form="...") not nested.
  const getIdField = () =>
    document.querySelector('select[name="id"][id*="variant-select"]') ||
    document.querySelector('form[action*="/cart/add"] [name="id"]') ||
    document.querySelector('[name="id"]');

  const getCartForm = () => document.querySelector('form[action*="/cart/add"]');

  // Make the dynamic checkout button re-evaluate for the resolved variant.
  const syncCheckoutButton = (variant) => {
    const form = getCartForm();

    // A bubbling change on the FORM reaches form-level listeners even when the
    // id field is form-associated-but-not-nested (its own change won't bubble
    // up to the form). This is the key line the old file was missing.
    if (form) form.dispatchEvent(new Event("change", { bubbles: true }));

    // Best-effort: fire the theme's variant event. If THEME_VARIANT_EVENT is
    // wrong this simply no-ops -- see discovery notes at the bottom of the file.
    if (variant) {
      const target = document.querySelector("[data-custom-picker]") || document;
      target.dispatchEvent(
        new CustomEvent(THEME_VARIANT_EVENT, {
          bubbles: true,
          composed: true,
          detail: { variant },
        })
      );
    }

    // Fallback ONLY for a genuinely purchasable variant: clear a stuck disabled
    // state on the accelerated checkout element. Gated on `available` so we never
    // light up a button for a variant that can't actually be ordered.
    if (variant && variant.available) {
      document
        .querySelectorAll("shopify-payment-button, shopify-accelerated-checkout")
        .forEach((el) => {
          el.removeAttribute("disabled");
          el.removeAttribute("aria-disabled");
          el.classList.remove("is-disabled");
        });
    }
  };

  const applyVariant = (variant) => {
    if (!variant) return;
    const idField = getIdField();
    if (idField) {
      idField.disabled = false;
      idField.value = String(variant.id);
      idField.dispatchEvent(new Event("change", { bubbles: true }));
    }
    syncCheckoutButton(variant);
  };

  const resolveAndApply = () => {
    const opts = getSelectedOptionValues();
    if (!opts) return;
    const variant = findMatchingVariant(opts);
    if (variant) applyVariant(variant);
  };

  window.addEventListener("DOMContentLoaded", async () => {
    productData = await loadProductJson();
    if (!productData) {
      console.warn("[saddle-variant-fix] could not load product JSON");
      return;
    }

    // Simple products (1 variant, no picker): set default id and stop.
    if (productData.variants.length === 1) {
      const defaultId = String(productData.variants[0].id);
      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        const idField = form.querySelector('[name="id"]');
        if (idField) {
          idField.value = defaultId;
          idField.disabled = false;
        }
      });
      return;
    }
  });

  // Sync as swatches are clicked. Deferred with rAF so the picker's own inline
  // onclick (which toggles .is-selected) finishes BEFORE we read the selection.
  // The old file read on capture-phase, i.e. one click stale -- this fixes that.
  document.addEventListener("click", (e) => {
    if (!e.target.closest(".block-swatch")) return;
    if (productData?.variants?.length === 1) return;
    requestAnimationFrame(resolveAndApply);
  });

  // Belt-and-suspenders: force the correct id right before submit to /cart/add.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.getAttribute("action")?.includes("/cart/add")) return;

      if (productData?.variants?.length === 1) {
        const idField = form.querySelector('[name="id"]');
        if (idField) {
          idField.disabled = false;
          idField.value = String(productData.variants[0].id);
        }
        return;
      }

      const opts = getSelectedOptionValues();
      const variant = opts ? findMatchingVariant(opts) : null;
      if (variant) {
        const idField = getIdField();
        if (idField) {
          idField.disabled = false;
          idField.value = String(variant.id);
        }
      }
    },
    true
  );
})();

/*
  ----------------------------------------------------------------------------
  DISCOVERING THE REAL EVENT NAME (do this once, then set THEME_VARIANT_EVENT)
  ----------------------------------------------------------------------------
  Buckles use the NATIVE picker, which fires the theme's real variant event.
  So go to a BUCKLE product page, open the console, paste this, then change the
  variant. Whatever name logs is the event to put in THEME_VARIANT_EVENT above:

    ["variant:change","variantChange","on:variant:change","variant-change"]
      .forEach(n => document.addEventListener(n, e =>
        console.log("FIRED:", n, e.detail), true));

  Also confirm whether the id field is nested or only form-associated (on a
  SADDLE page):

    const f = document.querySelector('form[action*="/cart/add"]');
    console.log("nested id?", !!f.querySelector('[name=id]'));
    console.log("assoc id?",  document.querySelector('[name=id]'));
    console.log("pay button:", document.querySelector('shopify-payment-button, shopify-accelerated-checkout'));
*/